// API クライアント。Cookie 認証のため credentials: 'include'。

export interface ApiError {
  error: string;
}

export class ApiRequestError extends Error {
  status: number;
  code?: string;
  required?: number;
  balance?: number;
  feature?: string;
  constructor(status: number, message: string, meta?: { code?: string; required?: number; balance?: number; feature?: string }) {
    super(message);
    this.status = status;
    this.code = meta?.code;
    this.required = meta?.required;
    this.balance = meta?.balance;
    this.feature = meta?.feature;
    this.name = "ApiRequestError";
  }
}

// レスポンス本文を JSON として解釈し、失敗レスポンスは ApiRequestError にする。
// 非JSONボディ(HTML エラーページ等)は status ベースの案内文にフォールバックする。
async function parseResponse<T>(res: Response, fallbackMsg: string): Promise<T> {
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = null; // 非JSONボディは無視して status ベースのメッセージにする。
    }
  }
  if (!res.ok) {
    const meta = data && typeof data === "object" ? data as Record<string, unknown> : {};
    const message =
      data && typeof data === "object" && "error" in data
        ? String((data as ApiError).error)
        : `${fallbackMsg} (${res.status})`;
    if (res.status === 402 && String(meta.code ?? "") === "credit_shortage" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("legalpages:credit-shortage", {
        detail: {
          message,
          required: Number(meta.required ?? 0),
          balance: Number(meta.balance ?? 0),
          feature: String(meta.feature ?? ""),
        },
      }));
    }
    throw new ApiRequestError(res.status, message, {
      code: typeof meta.code === "string" ? meta.code : undefined,
      required: typeof meta.required === "number" ? meta.required : undefined,
      balance: typeof meta.balance === "number" ? meta.balance : undefined,
      feature: typeof meta.feature === "string" ? meta.feature : undefined,
    });
  }
  return data as T;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse<T>(res, "エラーが発生しました");
}

// multipart/form-data アップロード(Content-Type はブラウザに任せる)。
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  return parseResponse<T>(res, "アップロードに失敗しました");
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
  upload,
};

export interface AuthUser {
  id: string;
  email: string;
  role: "customer" | "admin";
  last_name: string | null;
  first_name: string | null;
}
